import React from 'react'

import { Box, Button, Icon, Section } from '@factorialco/deprecated-design-system'
import { DataSchemaItemFragment, EmployeeDatumFieldFragment } from 'generated/resources/graphql'
import { EmployeesUpdateEmployeeDataDocument } from 'generated/resources/graphql'
import { keyBy, sortBy } from 'lodash'

import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { BooleanField } from './Fields/BooleanField'
import { DateField } from './Fields/DateField'
import { NumberField } from './Fields/NumberField'
import { StringField } from './Fields/StringField'
import { PayrollEmployeeDataForm } from './PayrollEmployeeDataForm'

interface Props {
  employeeId: number
  companyId: number
  employeePayrollDataSchema: DataSchemaItemFragment[]
  employeePayrollDatum: EmployeeDatumFieldFragment[]
  illustration: React.ComponentProps<typeof Icon.Large>['icon']
  title: string
  description: string
  readonly?: boolean
}

export const PayrollEmployeeData = observer(
  ({
    employeeId,
    companyId,
    employeePayrollDataSchema,
    employeePayrollDatum,
    illustration,
    title,
    description,
    readonly = false,
  }: Props) => {
    const fieldsData = keyBy(employeePayrollDatum, 'fieldName')
    const [disabled, setDisabled] = React.useState(false)
    const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({})

    const form = useUnsafeMemo(() => {
      return new PayrollEmployeeDataForm(employeePayrollDataSchema, fieldsData)
    }, [employeePayrollDataSchema])
    const curatedSchema = useUnsafeMemo(() => {
      const filteredSchema = employeePayrollDataSchema.filter((item) => !item.hidden)
      return sortBy(filteredSchema, 'position')
    }, [employeePayrollDataSchema])

    const [EmployeesUpdateEmployeeData] = useMutation(EmployeesUpdateEmployeeDataDocument, {
      extractorFn: (data) => data.payrollEmployeesMutations.upsertPayrollemployeesEmployeeDatum,
    })

    const handleValidationError = (fieldName: string, error: string | undefined) => {
      setValidationErrors((prev) => {
        if (error) {
          return { ...prev, [fieldName]: error }
        } else {
          const { [fieldName]: _removed, ...newErrors } = prev
          return newErrors
        }
      })
    }

    const hasValidationErrors = Object.keys(validationErrors).length > 0

    const saveEmployeeData = async () => {
      setDisabled(true)
      const result = await EmployeesUpdateEmployeeData({
        variables: {
          employeeId: employeeId,
          companyId: companyId,
          fields: form.serializedData,
        },
        refetchQueries: ['EmployeesGetEmployeeData'],
      })

      const handler = handleResult(result, (errors) => {
        Object.entries(errors).forEach(([fieldName, errorMessage]) => {
          const field = form.get(fieldName)
          if (field && errorMessage) {
            field.setErrors([errorMessage])
          }
        })
      })

      if (handler.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        form.cleanAll()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
      setDisabled(false)
    }

    if (curatedSchema.length === 0) {
      return null
    }

    return (
      <Section illustration={illustration} title={title} description={description}>
        <Box
          gap="s24"
          background="grey200"
          padding="s32"
          width="s360"
          alignSelf="flexEnd"
          borderRadius={{
            all: 'abs004',
          }}
        >
          {curatedSchema.map((item) => {
            switch (item.fieldType) {
              case 'boolean':
                return (
                  <BooleanField
                    key={item.fieldName}
                    dataSchemaItem={item}
                    field={form.get(item.fieldName)}
                    readonly={readonly}
                  />
                )
              case 'date':
                return (
                  <DateField
                    key={item.fieldName}
                    dataSchemaItem={item}
                    field={form.get(item.fieldName)}
                    readonly={readonly}
                  />
                )
              case 'integer':
                return (
                  <NumberField
                    key={item.fieldName}
                    dataSchemaItem={item}
                    field={form.get(item.fieldName)}
                    onValidationError={handleValidationError}
                    readonly={readonly}
                  />
                )
              case 'string':
                return (
                  <StringField
                    key={item.fieldName}
                    dataSchemaItem={item}
                    field={form.get(item.fieldName)}
                    onValidationError={handleValidationError}
                    readonly={readonly}
                  />
                )
            }
            return <div key={item.fieldName}>{item.fieldName}</div>
          })}
          {!readonly && form.isDirty && (
            <Button disabled={disabled || hasValidationErrors} onClick={() => saveEmployeeData()}>
              {i18n.t('common.submit')}
            </Button>
          )}
        </Box>
      </Section>
    )
  }
)
